// Thành phần giao diện dùng chung của các trang công lương. Kiểu dáng theo các trang hiện có của app
// (Công đoạn - Đơn giá, Quản lý lô...): khung trắng viền mảnh, nút chàm, bảng tiêu đề in hoa xám.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { X, ShieldAlert, Search, Check, Filter, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle } from 'lucide-react';

export const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100';
export const buttonPrimary = 'bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed';
export const buttonSecondary = 'px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed';
export const buttonDanger = 'px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed';
export const iconButton = 'p-1.5 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
export const ICON_TONES = {
    indigo: 'text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50',
    blue: 'text-blue-600 hover:text-blue-900 hover:bg-blue-50',
    green: 'text-green-600 hover:text-green-900 hover:bg-green-50',
    red: 'text-red-600 hover:text-red-900 hover:bg-red-50',
    gray: 'text-gray-500 hover:text-gray-800 hover:bg-gray-100',
    amber: 'text-amber-600 hover:text-amber-900 hover:bg-amber-50',
};

export const tableClass = 'min-w-full divide-y divide-gray-200';
export const theadClass = 'bg-gray-50';
export const thClass = 'px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
export const tbodyClass = 'bg-white divide-y divide-gray-200';
export const tdClass = 'px-3 py-3 text-sm text-gray-700';

export function PageShell({ icon: Icon, title, subtitle, actions, children }) {
    return (
        <div className="p-3 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border border-gray-100">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                {Icon && <Icon className="w-7 h-7 text-indigo-600" />}
                                {title}
                            </h1>
                            {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
                        </div>
                        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}

export function AccessDenied() {
    return (
        <div className="p-6 flex flex-col items-center justify-center text-center min-h-[50vh]">
            <ShieldAlert className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-gray-700 font-medium">Bạn chưa được cấp quyền xem trang này.</p>
            <p className="text-sm text-gray-500 mt-1">Liên hệ Admin để được cấp quyền.</p>
        </div>
    );
}

export function Spinner({ label = 'Đang tải dữ liệu...' }) {
    return (
        <div className="flex flex-col justify-center items-center py-12 gap-3">
            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-500">{label}</p>
        </div>
    );
}

export function EmptyState({ icon: Icon, title, children }) {
    return (
        <div className="text-center py-12 px-4">
            {Icon && <Icon className="w-12 h-12 text-gray-300 mx-auto mb-3" />}
            {title && <p className="text-gray-700 font-medium mb-1">{title}</p>}
            <div className="text-gray-500 text-sm">{children}</div>
        </div>
    );
}

const BADGE_TONES = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-800',
    indigo: 'bg-indigo-100 text-indigo-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    purple: 'bg-purple-100 text-purple-800',
    orange: 'bg-orange-100 text-orange-800',
};

export function Badge({ tone = 'gray', children, title }) {
    return <span title={title} className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${BADGE_TONES[tone]}`}>{children}</span>;
}

const STAT_TONES = {
    blue: ['bg-blue-50 border-blue-100', 'text-blue-700', 'text-blue-800'],
    indigo: ['bg-indigo-50 border-indigo-100', 'text-indigo-700', 'text-indigo-800'],
    green: ['bg-green-50 border-green-100', 'text-green-700', 'text-green-800'],
    purple: ['bg-purple-50 border-purple-100', 'text-purple-700', 'text-purple-800'],
    yellow: ['bg-yellow-50 border-yellow-100', 'text-yellow-700', 'text-yellow-800'],
    red: ['bg-red-50 border-red-100', 'text-red-700', 'text-red-800'],
};

/**
 * Thẻ thống kê. Thẻ có filterValue bấm được để lọc danh sách theo đúng con số đang xem; thẻ đang lọc
 * có viền chàm.
 * cards: [{ key, label, value, tone, caption, filterValue }]
 */
export function StatCards({ cards, active, onSelect }) {
    const columns = cards.length >= 5 ? 'lg:grid-cols-5' : cards.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
    return (
        <div className={`grid grid-cols-2 ${columns} gap-4 mb-6`}>
            {cards.map((card) => {
                const [box, label, value] = STAT_TONES[card.tone] || STAT_TONES.blue;
                const clickable = card.filterValue !== undefined && onSelect;
                const isActive = clickable && active === card.filterValue;
                const Tag = clickable ? 'button' : 'div';
                return (
                    <Tag key={card.key} type={clickable ? 'button' : undefined} onClick={clickable ? () => onSelect(card.filterValue) : undefined}
                        className={`${box} border rounded-lg p-4 text-left ${clickable ? 'hover:shadow-sm transition-shadow' : ''} ${isActive ? 'ring-2 ring-indigo-500' : ''}`}>
                        <h3 className={`text-sm ${label} mb-1`}>{card.label}</h3>
                        <p className={`text-2xl font-bold ${value}`}>{card.value}</p>
                        {card.caption && <p className={`text-xs ${label} mt-1 opacity-80`}>{card.caption}</p>}
                    </Tag>
                );
            })}
        </div>
    );
}

export function SearchInput({ value, onChange, placeholder }) {
    return (
        <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm" />
        </div>
    );
}

/** Thanh tìm kiếm + nút ẩn/hiện bộ lọc + khung bộ lọc, như các trang hiện có. */
export function ListToolbar({ search, onSearch, placeholder, filters, filterCount = 0, onClearFilters, extra }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="mb-4 space-y-3">
            <div className="flex flex-col md:flex-row gap-2">
                <SearchInput value={search} onChange={onSearch} placeholder={placeholder} />
                <div className="flex gap-2">
                    {filters && (
                        <button type="button" onClick={() => setOpen(!open)} className={buttonSecondary}>
                            <Filter className="w-4 h-4" />{open ? 'Ẩn bộ lọc' : 'Bộ lọc'}
                            {filterCount > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-indigo-600 text-white">{filterCount}</span>}
                        </button>
                    )}
                    {extra}
                </div>
            </div>
            {filters && open && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border">
                    {filters}
                    {onClearFilters && (
                        <div className="flex items-end">
                            <button type="button" onClick={onClearFilters} className={buttonSecondary}>Xóa bộ lọc</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function FilterField({ label, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {children}
        </div>
    );
}

/** Sắp xếp danh sách theo cột; bấm lần nữa để đảo chiều. accessors: { key: (row) => giá trị so sánh } */
export function useSortedRows(rows, accessors, initial = null) {
    const [sort, setSort] = useState(initial);
    const sorted = useMemo(() => {
        if (!sort || !accessors[sort.key]) return rows;
        const factor = sort.direction === 'asc' ? 1 : -1;
        const get = accessors[sort.key];
        return [...rows].sort((left, right) => {
            const a = get(left);
            const b = get(right);
            if (typeof a === 'number' && typeof b === 'number') return (a - b) * factor;
            if (a instanceof Date || b instanceof Date) return ((a?.getTime?.() ?? 0) - (b?.getTime?.() ?? 0)) * factor;
            return String(a ?? '').localeCompare(String(b ?? ''), 'vi') * factor;
        });
    }, [rows, sort, accessors]);
    const toggleSort = (key) => setSort((prev) => (prev?.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
    return { sorted, sort, toggleSort, setSort };
}

export function SortableTh({ label, sortKey, sort, onSort, align = 'left', className = '' }) {
    const Icon = sort?.key !== sortKey ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;
    return (
        <th scope="col" className={`${thClass} ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''} ${className}`}>
            <button type="button" onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 uppercase hover:text-gray-800 ${align === 'right' ? 'flex-row-reverse' : ''}`} title="Bấm để sắp xếp">
                {label}<Icon className={`w-3 h-3 ${sort?.key === sortKey ? 'text-indigo-600' : 'text-gray-400'}`} />
            </button>
        </th>
    );
}

/** Phân trang ở trình duyệt, cùng kiểu "Hiển thị: n mục / trang" của các trang hiện có. */
export function usePagination(rows, initialPerPage = 20) {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(initialPerPage);
    const totalPages = Math.max(1, Math.ceil(rows.length / perPage));
    useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);
    const start = (page - 1) * perPage;
    return {
        pageRows: rows.slice(start, start + perPage),
        pagination: { page, perPage, totalPages, total: rows.length, start, setPage, setPerPage },
    };
}

export function Pagination({ pagination, unit = 'mục' }) {
    const { page, perPage, totalPages, total, start, setPage, setPerPage } = pagination;
    if (!total) return null;
    return (
        <div className="flex flex-col md:flex-row justify-between items-center mt-4 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-700">
                Hiển thị:
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
                    {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
                {unit} / trang
            </div>
            <div className="text-sm text-gray-700">Hiển thị {start + 1} - {Math.min(start + perPage, total)} trong tổng số {total} {unit}</div>
            <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPage(page - 1)} disabled={page === 1}
                    className={`p-2 rounded-lg border ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`} aria-label="Trang trước">
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700">Trang {page} / {totalPages}</span>
                <button type="button" onClick={() => setPage(page + 1)} disabled={page === totalPages}
                    className={`p-2 rounded-lg border ${page === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`} aria-label="Trang sau">
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

export function IconAction({ icon: Icon, title, onClick, tone = 'indigo', disabled }) {
    return (
        <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled} className={`${iconButton} ${ICON_TONES[tone]}`}>
            <Icon className="h-4 w-4" />
        </button>
    );
}

export function Modal({ title, subtitle, icon: Icon, onClose, children, footer, size = 'max-w-2xl', disabled }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} className={`bg-white rounded-xl shadow-xl w-full ${size} max-h-[90vh] overflow-hidden flex flex-col`}>
                <div className="flex justify-between items-start border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">{Icon && <Icon className="w-5 h-5 text-indigo-600" />}{title}</h2>
                        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} disabled={disabled} className="text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50" aria-label="Đóng">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
                {footer && <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">{footer}</div>}
            </div>
        </div>
    );
}

/** Hộp xác nhận thay cho window.confirm; confirmText bắt gõ lại (vd mã kỳ) với thao tác không hoàn tác được. */
export function ConfirmDialog({ title, message, confirmLabel = 'Xác nhận', danger = true, confirmText, onConfirm, onClose }) {
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    const blocked = confirmText && typed !== confirmText;
    return (
        <Modal title={title} icon={AlertTriangle} onClose={onClose} disabled={busy} size="max-w-md"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={busy}>Hủy</button>
                    <button className={danger ? buttonDanger : buttonPrimary} disabled={busy || blocked}
                        onClick={async () => { try { setBusy(true); await onConfirm(); } finally { setBusy(false); } }}>
                        {busy ? 'Đang xử lý...' : confirmLabel}
                    </button>
                </>
            )}>
            <div className="text-sm text-gray-700 space-y-3">
                <div>{message}</div>
                {confirmText && (
                    <Field label={`Gõ ${confirmText} để xác nhận`}>
                        <input className={inputClass} value={typed} onChange={(e) => setTyped(e.target.value)} />
                    </Field>
                )}
            </div>
        </Modal>
    );
}

export function Field({ label, required, hint, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            {children}
            {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        </div>
    );
}

/**
 * Chuỗi người dùng gõ theo kiểu Việt Nam (chấm hàng nghìn, phẩy thập phân) → chuỗi số chuẩn
 * "8000000" / "1.5", rỗng nếu chưa có chữ số. Dấu chấm luôn là hàng nghìn: ô nhập tự chèn
 * dấu chấm nên người dùng không cần gõ, còn "1.500" phải là một nghìn rưỡi chứ không phải 1,5.
 */
export function parseTypedNumber(text) {
    const raw = String(text ?? '').replace(/\s|\./g, '');
    const negative = raw.startsWith('-');
    const [integer = '', ...decimals] = raw.replace(/[^\d,]/g, '').split(',');
    const decimal = decimals.join('');
    if (!integer && !decimal) return '';
    return `${negative ? '-' : ''}${integer.replace(/^0+(?=\d)/, '') || '0'}${decimal ? `.${decimal}` : ''}`;
}

/** Chuỗi hiển thị trong ô: giữ phần người dùng đang gõ dở (dấu phẩy cuối, dấu trừ đầu). */
export function formatTypedNumber(text) {
    const raw = String(text ?? '').replace(/\s|\./g, '');
    const negative = raw.startsWith('-') ? '-' : '';
    const [integer = '', ...decimals] = raw.replace(/[^\d,]/g, '').split(',');
    const grouped = integer.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negative}${grouped}${decimals.length ? `,${decimals.join('')}` : ''}`;
}

/** Số chuẩn ("1500", "1.5" hoặc số) → chuỗi hiển thị "1.500" / "1,5". */
export function displayNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    return formatTypedNumber(String(value).replace('.', ','));
}

/**
 * Ô nhập số tự chèn dấu chấm hàng nghìn khi gõ. value / onChange dùng chuỗi số chuẩn
 * (parseSheetNumber đọc được, không nhầm hàng nghìn với thập phân); con trỏ giữ đúng chỗ
 * sau khi chèn dấu để sửa giữa chuỗi không bị nhảy về cuối.
 */
export function NumberInput({ value, onChange, className = inputClass, ...props }) {
    const inputRef = useRef(null);
    const caretRef = useRef(null);
    const [text, setText] = useState(() => displayNumber(value));

    useEffect(() => {
        if (parseTypedNumber(text) !== String(value ?? '')) setText(displayNumber(value));
        // Chỉ đồng bộ khi giá trị bên ngoài đổi (mở form khác, xóa form), không phải mỗi lần gõ.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useLayoutEffect(() => {
        if (caretRef.current === null || !inputRef.current) return;
        // Đặt con trỏ sau đúng số ký tự có nghĩa (chữ số, phẩy, trừ) đã đứng trước nó lúc gõ.
        let seen = 0;
        let position = 0;
        while (position < text.length && seen < caretRef.current) {
            if (/[\d,-]/.test(text[position])) seen += 1;
            position += 1;
        }
        inputRef.current.setSelectionRange(position, position);
        caretRef.current = null;
    }, [text]);

    const handleChange = (event) => {
        const raw = event.target.value;
        const caret = event.target.selectionStart ?? raw.length;
        caretRef.current = raw.slice(0, caret).replace(/[^\d,-]/g, '').length;
        const next = formatTypedNumber(raw);
        setText(next);
        onChange(parseTypedNumber(next));
    };

    return <input ref={inputRef} inputMode="decimal" className={className} value={text} onChange={handleChange} {...props} />;
}

export function SectionTitle({ children }) {
    return <h3 className="text-sm font-semibold text-gray-800 mb-2 pb-1 border-b border-gray-100">{children}</h3>;
}

export function MessageList({ tone = 'red', items }) {
    if (!items?.length) return null;
    const tones = { red: 'bg-red-50 border-red-200 text-red-700', yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800', blue: 'bg-blue-50 border-blue-200 text-blue-800', green: 'bg-green-50 border-green-200 text-green-800' };
    return (
        <ul className={`text-sm border rounded-lg p-3 space-y-1 ${tones[tone]}`}>
            {items.map((item, index) => <li key={index}>• {item}</li>)}
        </ul>
    );
}

const normalizeSearch = (value) => String(value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

export function matchesSearch(text, query) {
    if (!query) return true;
    return normalizeSearch(text).includes(normalizeSearch(query));
}

/**
 * Chọn một hoặc nhiều mục trong danh sách dài (nhân sự, tổ) có ô tìm kiếm không dấu.
 * options: [{ value, label, sublabel }]
 */
export function SearchSelect({ options, value, onChange, multiple = false, placeholder = 'Tìm...', disabled, maxHeight = 'max-h-56' }) {
    const [query, setQuery] = useState('');
    const selected = useMemo(() => new Set(multiple ? value : value ? [value] : []), [multiple, value]);
    const visible = options.filter((option) => matchesSearch(`${option.label} ${option.sublabel ?? ''} ${option.value}`, query));
    const toggle = (optionValue) => {
        if (disabled) return;
        if (!multiple) return onChange(optionValue === value ? '' : optionValue);
        onChange(selected.has(optionValue) ? value.filter((item) => item !== optionValue) : [...value, optionValue]);
    };
    return (
        <div className={`border border-gray-300 rounded-lg ${disabled ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="relative border-b border-gray-200">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} disabled={disabled}
                    className="w-full pl-8 pr-2 py-2 text-sm rounded-t-lg focus:outline-none" />
            </div>
            <div className={`${maxHeight} overflow-y-auto`}>
                {visible.length === 0 ? (
                    <div className="text-center py-4 text-xs text-gray-400">Không có mục phù hợp</div>
                ) : visible.map((option) => {
                    const isSelected = selected.has(option.value);
                    return (
                        <button type="button" key={option.value} onClick={() => toggle(option.value)} disabled={disabled}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm border-b border-gray-50 last:border-0 ${isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
                            <span className={`w-4 h-4 flex-shrink-0 flex items-center justify-center border-2 ${multiple ? 'rounded' : 'rounded-full'} ${isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                            </span>
                            <span className={isSelected ? 'font-semibold text-indigo-900' : 'text-gray-700'}>{option.label}</span>
                            {option.sublabel && <span className="ml-auto text-xs text-gray-400">{option.sublabel}</span>}
                        </button>
                    );
                })}
            </div>
            {multiple && value.length > 0 && (
                <div className="px-3 py-1.5 border-t border-gray-200 text-xs text-gray-600 flex justify-between">
                    <span>Đã chọn {value.length}</span>
                    {!disabled && <button type="button" onClick={() => onChange([])} className="text-red-600 hover:underline">Bỏ chọn hết</button>}
                </div>
            )}
        </div>
    );
}

export const formatMoney = (value) => (value === null || value === undefined || value === '' ? '' : Number(value).toLocaleString('vi-VN'));
export const formatDay = (date) => (date ? date.toLocaleDateString('vi-VN') : '');
export const formatDateTime = (date) => (date ? date.toLocaleString('vi-VN') : '');

/** Giá trị cho input type="date" (YYYY-MM-DD theo giờ máy) và ngược lại. */
export function toInputDate(date) {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function fromInputDate(value) {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** Nhân sự thành lựa chọn cho SearchSelect. */
export function employeeOptions(employees) {
    return employees
        .filter((employee) => employee.SO_HD)
        .slice()
        .sort((left, right) => String(left.HO_TEN).localeCompare(String(right.HO_TEN), 'vi'))
        .map((employee) => ({ value: employee.SO_HD, label: employee.HO_TEN || employee.SO_HD, sublabel: [employee.SO_HD, employee.TO].filter(Boolean).join(' · ') }));
}
